import { FC, useRef, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { getCorrelationID, hyphenateForTagular } from "@/utils/tagular/helpers";
import { Button } from "../../../ui/button";
import EventLink from "@/components/Link/EventLink/EventLink";
import useIsMobile from "@/hooks/useIsMobile";
import { SEARCH_BAR_FOOTER_CTA_HTML_ID } from "./searchBarEventing";
import useTranslations from "@/i18n/useTranslations";

type FooterProps = {
  text?: string;
  url?: string;
  linkText?: string;
  isError?: boolean;
  onRetry?: () => void;
  location?: string;
};

const sharedClassName = "rounded-b-[12px]";
const sharedStyle = {
  background:
    "linear-gradient(360deg, #FFFFFF 48.98%, hsl(var(--gray-100)) 161.22%)",
};

const SearchModalFooterTextView: FC<{
  text: string | undefined;
  location: string;
}> = ({ text, location }) => {
  const viewedRef = useRef(false);

  const webElement = {
    elementType: ElementType.Button,
    htmlId: SEARCH_BAR_FOOTER_CTA_HTML_ID,
    location,
    text: text ?? "",
    name: hyphenateForTagular(text ?? ""),
  };

  const { elementViewed } = useElementEvents({
    correlationId: getCorrelationID(),
    webElement,
  });
  const { ref: viewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      viewedRef.current = true;
      elementViewed();
    }
  }, [elementViewed, inView]);

  return (
    <span
      ref={viewRef}
      className="elm-p2 text-center text-primary-500 text-balance font-bold"
    >
      {text}
    </span>
  );
};

const Footer: FC<FooterProps> = ({
  text,
  url,
  linkText,
  isError,
  onRetry,
  location = "embedded-search",
}) => {
  const isMobile = useIsMobile();

  if (isError) {
    return (
      <div id="footer" className={sharedClassName} style={sharedStyle}>
        <ErrorFooterContent location={location} onRetry={onRetry} />
      </div>
    );
  }

  const useInlineLink =
    !isMobile && url && linkText && text?.includes(linkText);

  if (useInlineLink) {
    const index = text!.indexOf(linkText);
    const before = text!.slice(0, index);
    const after = text!.slice(index + linkText.length);

    return (
      <div id="footer" className={sharedClassName} style={sharedStyle}>
        <div className="w-full min-h-[46px] py-3 flex items-center justify-center px-4">
          <span className="elm-p2 text-center text-primary-500 text-balance font-bold">
            {before}
            <EventLink
              href={url}
              text={linkText}
              html_id={SEARCH_BAR_FOOTER_CTA_HTML_ID}
              location={location}
              element_type={ElementType.Button}
              stickyElementView
              class_name="underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            />
            {after}
          </span>
        </div>
      </div>
    );
  }

  if (url) {
    return (
      <div id="footer" className={sharedClassName} style={sharedStyle}>
        <EventLink
          href={url}
          text={text ?? ""}
          html_id={SEARCH_BAR_FOOTER_CTA_HTML_ID}
          location={location}
          element_type={ElementType.Button}
          stickyElementView
          class_name="block w-full min-h-[46px] py-3 flex items-center justify-center px-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 rounded-b-[12px]"
          visible_text={
            <span className="elm-p2 text-center text-primary-500 text-balance font-bold">
              {text}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div id="footer" className={sharedClassName} style={sharedStyle}>
      <div className="w-full min-h-[46px] py-3 flex items-center justify-center px-4">
        <SearchModalFooterTextView text={text} location={location} />
      </div>
    </div>
  );
};

const ErrorFooterContent: FC<{ location?: string; onRetry?: () => void }> = ({
  location = "embedded-search",
  onRetry,
}) => {
  const t = useTranslations("ErrorPages");
  const hasViewedRef = useRef(false);

  const webElement = {
    elementType: ElementType.Entry,
    htmlId: "search-error-button",
    location: location,
    name: `${location}-button`,
    text: "Try again",
  };
  const { elementClicked, elementViewed } = useElementEvents({
    correlationId: getCorrelationID(),
    webElement,
  });

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div className="w-full flex justify-center py-4">
      <Button
        ref={inViewRef}
        onClick={() => {
          elementClicked();
          onRetry?.();
        }}
      >
        {t("tryAgain")}
      </Button>
    </div>
  );
};

export default Footer;
